import { API_URL } from '../config/api';

let sent = false;
let lcpValue = null;

// Diagnostic flags — set by other modules, sent with perf payload
export const perfDiag = {
    authEarly: false,   // true if early auth from index.html was used
};

// Safe performance.mark wrapper — never throws
export function perfMark(name) {
    try {
        performance.mark('perf:' + name);
    } catch (_) {
        // ignore
    }
}

// Observe LCP (Largest Contentful Paint)
try {
    const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
            lcpValue = entries[entries.length - 1].startTime;
        }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
} catch (_) {
    // PerformanceObserver not supported
}

// Compute duration between two marks, returns ms or null
function duration(startMark, endMark) {
    try {
        const starts = performance.getEntriesByName('perf:' + startMark, 'mark');
        const ends = performance.getEntriesByName('perf:' + endMark, 'mark');
        if (starts.length > 0 && ends.length > 0) {
            return Math.round(ends[0].startTime - starts[0].startTime);
        }
    } catch (_) {
        // ignore
    }
    return null;
}

// Get mark timestamp relative to navigation start
function markTime(name) {
    try {
        const entries = performance.getEntriesByName('perf:' + name, 'mark');
        if (entries.length > 0) {
            return Math.round(entries[0].startTime);
        }
    } catch (_) {
        // ignore
    }
    return null;
}

// Detect platform from Telegram WebApp or user agent
function detectPlatform() {
    if (window.Telegram?.WebApp?.platform) {
        return window.Telegram.WebApp.platform;
    }
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    if (/macintosh/.test(ua)) return 'macos';
    if (/windows/.test(ua)) return 'windows';
    return 'unknown';
}

// Detect connection type
function detectConnection() {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn?.effectiveType) return conn.effectiveType;
    return 'unknown';
}

// Collect all metrics and POST to backend (once only)
export function perfSend() {
    if (sent) return;
    sent = true;

    try {
        // Get FCP
        let fcp = null;
        const fcpEntries = performance.getEntriesByName('first-contentful-paint', 'paint');
        if (fcpEntries.length > 0) {
            fcp = Math.round(fcpEntries[0].startTime);
        }

        const fields = {};

        const htmlParse = markTime('htmlParsed');
        if (htmlParse != null) fields.html_parse = htmlParse;

        const jsBundle = duration('htmlParsed', 'reactMount');
        if (jsBundle != null) fields.js_bundle = jsBundle;

        const tgInit = duration('reactMount', 'tgInit');
        if (tgInit != null) fields.tg_init = tgInit;

        const auth = duration('tgInit', 'authComplete');
        if (auth != null) fields.auth = auth;

        const sseConnect = duration('authComplete', 'sseConnected');
        if (sseConnect != null) fields.sse_connect = sseConnect;

        const dataFetch = duration('authComplete', 'dataLoaded');
        if (dataFetch != null) fields.data_fetch = dataFetch;

        // data_fetch sub-stages
        const dfWait = duration('authComplete', 'fetchStart');
        if (dfWait != null) fields.df_wait = dfWait;

        const dfServer = duration('fetchStart', 'fetchHeaders');
        if (dfServer != null) fields.df_server = dfServer;

        const dfParse = duration('fetchHeaders', 'fetchResponse');
        if (dfParse != null) fields.df_parse = dfParse;

        const dfGap = duration('fetchResponse', 'buildStart');
        if (dfGap != null) fields.df_gap = dfGap;

        const dfBuild = duration('buildStart', 'dataLoaded');
        if (dfBuild != null) fields.df_build = dfBuild;

        const resources = duration('dataLoaded', 'resourcesLoaded');
        if (resources != null) fields.resources = resources;

        const total = markTime('appRendered');
        if (total != null) fields.total = total;

        if (fcp != null) fields.fcp = fcp;
        if (lcpValue != null) fields.lcp = Math.round(lcpValue);

        // Must have total to be useful
        if (fields.total == null) {
            sent = false;
            return;
        }

        // Diagnostic: track auth method
        fields.auth_early = perfDiag.authEarly ? 1 : 0;

        const payload = {
            tags: {
                platform: detectPlatform(),
                is_telegram: String(Boolean(window.Telegram?.WebApp?.initData)),
                connection_type: detectConnection(),
            },
            fields,
        };

        fetch(API_URL + 'metrics/perf', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {
            // ignore send errors
        });
    } catch (_) {
        sent = false;
    }
}
